// components/collab-projects/CollabProjectsButton.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { AiOutlineShareAlt } from "react-icons/ai";
import { FiX } from "react-icons/fi";

import CollabProjectTree, {
  CollabProjectsFilter,
} from "./CollabProjectTree";
import {
  projectUrl,
  useCollabProjects,
} from "./CollabProjectsProvider";
import "./collab-projects.scss";

export default function CollabProjectsButton() {
  const [mode, setMode] = useState(null);
  const [position, setPosition] = useState(null);

  const anchor = useRef(null);
  const trigger = useRef(null);
  const panel = useRef(null);
  const timer = useRef(null);

  const { pathname } = useLocation();
  const { projects } = useCollabProjects();

  const selected = projects.some((project) =>
    pathname.startsWith(projectUrl(project).slice(0, -1)),
  );

  const cancelClose = () => window.clearTimeout(timer.current);

  const close = useCallback(() => {
    window.clearTimeout(timer.current);
    setMode(null);
    setPosition(null);
  }, []);

  const leave = () => {
    cancelClose();

    timer.current = window.setTimeout(() => {
      if (document.querySelector(".tiny-context-menu__menu")) return;

      setMode((value) => (value === "hover" ? null : value));
    }, 160);
  };

  useEffect(() => {
    close();
  }, [pathname, close]);

  useEffect(
    () => () => window.clearTimeout(timer.current),
    [],
  );

  useLayoutEffect(() => {
    if (!mode) return;

    const place = () => {
      const button = anchor.current.getBoundingClientRect();
      const rect = panel.current.getBoundingClientRect();

      const left = Math.max(
        8,
        Math.min(
          button.right + 10,
          window.innerWidth - rect.width - 8,
        ),
      );

      const top = Math.max(
        8,
        Math.min(
          button.top - 4,
          window.innerHeight - rect.height - 8,
        ),
      );

      setPosition({
        left,
        top,
        tabLeft: button.left - 4,
        tabTop: button.top - 4,
        connected: Math.abs(left - (button.right + 10)) < 1,
      });
    };

    place();

    const resize = new ResizeObserver(place);
    resize.observe(panel.current);
    resize.observe(anchor.current);

    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);

    const dismiss = (event) => {
      if (event.key === "Escape") {
        close();
        trigger.current?.focus();
      }
    };

    const outside = (event) => {
      if (
        event.target.closest?.(
          ".tiny-context-menu__menu, .tiny-context-menu__backdrop",
        )
      ) {
        return;
      }

      if (
        !panel.current?.contains(event.target) &&
        !trigger.current?.contains(event.target)
      ) {
        close();
      }
    };

    document.addEventListener("keydown", dismiss);
    document.addEventListener("pointerdown", outside);

    return () => {
      resize.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("keydown", dismiss);
      document.removeEventListener("pointerdown", outside);
    };
  }, [mode, close]);

  return (
    <>
      <div ref={anchor} className="collab-launcher">
        <button
          ref={trigger}
          type="button"
          className={`collab-launcher__button${
            mode ? " is-open" : ""
          }${selected ? " is-selected" : ""}`}
          aria-label="Collab Projects"
          aria-expanded={Boolean(mode)}
          aria-controls={mode ? "collab-projects-panel" : undefined}
          onPointerEnter={(event) => {
            if (event.pointerType === "mouse") {
              cancelClose();
              setMode((value) => value || "hover");
            }
          }}
          onPointerLeave={leave}
          onClick={(event) => {
            cancelClose();
            setMode((value) =>
              value === "pinned" ? null : "pinned",
            );

            if (event.detail === 0 && mode !== "pinned") {
              requestAnimationFrame(() =>
                panel.current?.querySelector("button")?.focus(),
              );
            }
          }}
        >
          <AiOutlineShareAlt />
        </button>
      </div>

      {mode &&
        createPortal(
          <>
            {position?.connected && (
              <span
                className="collab-launcher__portal-tab"
                aria-hidden="true"
                style={{
                  left: position.tabLeft,
                  top: position.tabTop,
                }}
              >
                <AiOutlineShareAlt />
              </span>
            )}

            {position?.connected && (
              <svg
                className="collab-panel__connector"
                aria-hidden="true"
                style={{
                  left: position.tabLeft + 32,
                  top: position.tabTop + 32,
                }}
                width={
                  position.left +
                  13 -
                  (position.tabLeft + 32)
                }
                height="1"
              >
                <path
                  d={`M 0 0 C 12 0 12 ${
                    position.top +
                    22 -
                    position.tabTop -
                    32
                  } ${
                    position.left +
                    13 -
                    position.tabLeft -
                    32
                  } ${
                    position.top +
                    22 -
                    position.tabTop -
                    32
                  }`}
                />
              </svg>
            )}

            <section
              ref={panel}
              id="collab-projects-panel"
              className="collab-panel"
              aria-label="Collab Projects"
              style={{
                left: position?.left ?? 64,
                top: position?.top ?? 8,
                visibility: position ? "visible" : "hidden",
              }}
              onPointerEnter={cancelClose}
              onPointerLeave={leave}
              onFocusCapture={() => {
                cancelClose();
                setMode("pinned");
              }}
            >
              <header>
                <strong>
                  <span className="collab-dot" aria-hidden="true" />
                  Collab Projects
                </strong>

                <CollabProjectsFilter />

                <button
                  type="button"
                  aria-label="Close Collab Projects"
                  onClick={() => {
                    close();
                    trigger.current?.focus();
                  }}
                >
                  <FiX />
                </button>
              </header>

              <div className="collab-panel__body">
                <CollabProjectTree
                  heading={false}
                  onNavigate={close}
                />
              </div>
            </section>
          </>,
          document.body,
        )}
    </>
  );
}